'use client';

/**
 * ProductPricePositionCard — where the selected product's list price sits
 * among the priced products of its own type: ten price bands, the ladder's
 * budget / mid / premium marks, and a separate "On request" bar for products
 * with no real price (zero or the $1 placeholders), which are never ranked.
 *
 * Keyed by the selected PT_* product, so it shows wherever a product is
 * selected — not only after a ladder click. A renderer: every position, bar
 * height and count arrives from the BFF (`paint` holds the drawing numbers).
 *
 * @see klyde_edge/klyde_intelligence/services/price_position.py
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_price_position"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '../rail-card';

interface Band { lo: number | null; hi: number | null; count: number; paint: { x0_pct: number; w_pct: number; bar_pct: number } }

interface PricePosition {
  state: 'ok' | 'unknown_product' | 'no_category' | 'no_price' | 'other_currency' | 'too_few';
  grain: 'product_type' | 'category' | null;
  list_price: number | null;
  item_on_request: boolean;
  priced_count: number;
  cheaper_count: number | null;
  item_band: number | null;
  on_request_count: number;
  bands: Band[];
  on_request: { count: number; paint: { x0_pct: number; w_pct: number; bar_pct: number; broken: boolean } } | null;
  paint: {
    axis_x0_pct: number;
    item_x_pct: number | null;
    axis_ticks: { value: number; x_pct: number }[];
    tier_marks: { tier: 'budget' | 'mid' | 'premium'; x_pct: number }[];
  } | null;
  labels: Record<string, string>;
}

const PLOT_H = 96;   // px of bar area; bar heights arrive as percentages of it
const money = (v: number) => `$${v.toLocaleString()}`;
const short = (v: number) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`);
const AMBER = '#e0a94f';

// Which way a tier label runs from its line, so neighbouring labels don't collide.
const TIER_ALIGN = { budget: '-translate-x-full pr-1', mid: '-translate-x-1/2', premium: 'pl-1' } as const;

export function ProductPricePositionCard() {
  const { entityType, entityId } = useViewportSelection();
  const strategy = useCapabilityStore((s) => s.strategyName('catalog', 'product_price_position'));
  const enabled = entityType === 'product' && !!entityId && entityId.startsWith('PT_');
  const { data, isLoading } = useStrategyData<PricePosition>(
    strategy, { semantic_id: entityId ?? '' }, enabled && !!strategy,
  );
  const d = data?.data;

  useCardExport(useMemo(() => ({
    label: 'Price position',
    content_key: 'product_price_position',
    surface: 'rail' as const,
    status: (!enabled ? 'idle' : d ? 'loaded' : isLoading ? 'loading' : 'idle') as 'idle' | 'loaded' | 'loading',
    query_key: enabled && strategy ? ['strategy-data', strategy, { semantic_id: entityId ?? '' }] : null,
    text: d ? `${d.cheaper_count ?? '—'} of ${d.priced_count} priced products cost less` : 'Price position',
    json: d ? { state: d.state, list_price: d.list_price, priced_count: d.priced_count, cheaper_count: d.cheaper_count, on_request_count: d.on_request_count } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [enabled, d, isLoading, strategy, entityId]));

  if (!enabled || !d) return null;
  const L = d.labels;

  if (d.state === 'unknown_product' || d.state === 'no_category') return null;
  if (d.state !== 'ok' || !d.paint) {
    const quiet = { too_few: L.too_few, no_price: L.no_price, other_currency: L.other_currency }[d.state];
    return (
      <RailCard title={L.title} icon={BarChart3}>
        <div className={cn(text.muted)}>{quiet}</div>
      </RailCard>
    );
  }

  const paint = d.paint;
  const bandLabel = (b: Band) =>
    b.lo == null ? `${L.under} ${short(b.hi as number)}` : b.hi == null ? `${short(b.lo)} ${L.and_over}` : `${short(b.lo)}–${short(b.hi)}`;
  const itemBand = d.item_band != null ? d.bands[d.item_band] : null;

  return (
    <RailCard title={L.title} icon={BarChart3}>
      <div className="relative w-full" style={{ height: PLOT_H + 64 }} role="img"
        aria-label={`${d.priced_count} priced products in 10 price bands, ${d.on_request_count} price on request`}>
        {/* the plot: bars stand on its floor and are a percentage of its height */}
        <div className="absolute inset-x-0 border-b border-border" style={{ top: 22, height: PLOT_H }}>
          {/* the On-request bar, left of the price axis */}
          {d.on_request && (
            <>
              <div
                className={cn('absolute bottom-0 rounded-sm border text-muted-foreground', d.item_on_request ? 'border-transparent' : 'border-current')}
                title={`${d.on_request.count.toLocaleString()} ${L.on_request_word}`}
                style={{
                  left: `${d.on_request.paint.x0_pct}%`, width: `${d.on_request.paint.w_pct}%`, height: `${d.on_request.paint.bar_pct}%`,
                  background: d.item_on_request ? AMBER : 'repeating-linear-gradient(135deg, transparent 0 3px, color-mix(in srgb, currentColor 55%, transparent) 3px 4px)',
                }}
              >
                {d.on_request.paint.broken && (
                  <span className="absolute -left-1 -right-1 h-[5px] bg-card" style={{ top: '28%', transform: 'rotate(-14deg)' }} />
                )}
              </div>
              <span className={cn(text.muted, 'absolute tabular-nums whitespace-nowrap')}
                style={{ left: 0, bottom: `calc(${d.on_request.paint.bar_pct}% + 2px)` }}>
                {d.on_request.count.toLocaleString()}
              </span>
              {d.item_on_request && (
                <span className={cn(text.muted, 'absolute font-semibold')} style={{ left: 0, bottom: `calc(${d.on_request.paint.bar_pct}% + 16px)`, color: AMBER }}>
                  {L.this_word}
                </span>
              )}
            </>
          )}

          {/* the ten price bands */}
          {d.bands.map((b, i) => (
            <div key={i} title={`${bandLabel(b)}: ${b.count.toLocaleString()}`}
              className={cn('absolute bottom-0 rounded-sm', i === d.item_band ? '' : 'bg-primary/40')}
              style={{ left: `${b.paint.x0_pct}%`, width: `${b.paint.w_pct}%`, height: `${b.paint.bar_pct}%`, padding: '0 1.5px', backgroundClip: 'content-box', background: i === d.item_band ? AMBER : undefined }} />
          ))}

          {/* the selected product's price, above its band */}
          {itemBand && paint.item_x_pct != null && d.list_price != null && (
            <span className={cn(text.muted, 'absolute z-20 -translate-x-1/2 rounded-sm bg-card px-1 font-semibold whitespace-nowrap')}
              style={{ left: `${paint.item_x_pct}%`, bottom: `calc(${itemBand.paint.bar_pct}% + 2px)`, color: AMBER }}>
              {money(d.list_price)}
            </span>
          )}

          {/* tier marks: dashed line, label above the plot */}
          {paint.tier_marks.map((t) => (
            <div key={t.tier} className="pointer-events-none absolute z-10 border-l border-dashed border-foreground/50" style={{ left: `${t.x_pct}%`, top: -6, bottom: 0 }}>
              <span className={cn(text.muted, 'absolute -top-4 left-0 whitespace-nowrap font-semibold', TIER_ALIGN[t.tier])}>{L[t.tier]}</span>
            </div>
          ))}
        </div>

        {/* price axis, and the On-request label under its bar */}
        <div className="absolute inset-x-0" style={{ top: 22 + PLOT_H + 4 }}>
          {paint.axis_ticks.map((t) => (
            <span key={t.value} className={cn(text.muted, 'absolute -translate-x-1/2 tabular-nums')} style={{ left: `${t.x_pct}%` }}>{short(t.value)}</span>
          ))}
          {d.on_request && (
            <span className={cn(text.muted, 'absolute whitespace-nowrap text-right')} style={{ right: `${100 - paint.axis_x0_pct}%` }}>
              {L.on_request_word}
            </span>
          )}
        </div>
      </div>
      <div className={cn(text.muted)}>{L.y_caption}</div>

      <div className="mt-3 flex flex-col gap-1">
        {d.item_on_request ? (
          <>
            <div className={cn(text.body)}>{L.on_request_item}</div>
            <div className={cn(text.muted)}>{d.on_request_count.toLocaleString()} {L.on_request_note}</div>
          </>
        ) : (
          <>
            <div className={cn(text.body)}>
              <b className="tabular-nums">{d.cheaper_count?.toLocaleString()}</b> {L.cheaper_lead}{' '}
              <b className="tabular-nums">{d.priced_count.toLocaleString()}</b> {L.priced_products}.{' '}
              {itemBand && <><b className="tabular-nums">{itemBand.count.toLocaleString()}</b> {L.same_band}.</>}
            </div>
            <div className={cn(text.muted)}>{L.not_counted} · {d.on_request_count.toLocaleString()}</div>
          </>
        )}
        {d.grain === 'category' && <div className={cn(text.muted)}>{L.grain_category}</div>}
      </div>
    </RailCard>
  );
}
